
import { useState } from 'react';

interface CaseStudy {
  plan: string;
  planKey: 'launch' | 'full' | 'management';
  icon: string;
  badgeColor: string;
  badgeBg: string;
  borderColor: string;
  accentColor: string;
  owner: string;
  location: string;
  propertyType: string;
  period: string;
  challenge: string;
  result: string;
  stats: { label: string; value: string }[];
  quote: string;
  image: string;
}

const caseStudies: CaseStudy[] = [
  {
    plan: '開業サポート',
    planKey: 'launch',
    icon: 'ri-file-list-3-line',
    badgeColor: 'text-slate-700',
    badgeBg: 'bg-slate-100',
    borderColor: 'border-slate-300',
    accentColor: '#475569',
    owner: 'T.Kさん（40代・会社員）',
    location: '東京都品川区',
    propertyType: '1LDK マンション',
    period: '2024年3月〜',
    challenge:
      '副業として民泊を始めたかったが、申請手続きが複雑で何から始めればいいか全くわからなかった。費用も抑えたかったので、自分でできる部分は自分でやりたかった。',
    result:
      '申請書類の作成・保健所対応・Airbnbページ立ち上げまでサポート。開業まで約2ヶ月で完了し、初月から黒字化を達成。',
    stats: [
      { label: '開業までの期間', value: '約2ヶ月' },
      { label: '初月売上', value: '¥98,000' },
      { label: '稼働率（3ヶ月平均）', value: '62%' },
      { label: '初期費用', value: '¥320,000' },
    ],
    quote:
      '申請の流れが全く見えていたのに、ステップごとに丁寧に教えてもらえて安心できました。自分でも動けるようになった気がします。',
    image:
      'https://public.readdy.ai/ai/img_res/f517ff3d2d53e3173ed5bd9bbda90ebb.jpg',
  },
  {
    plan: '開業＋運営設計',
    planKey: 'full',
    icon: 'ri-rocket-line',
    badgeColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    borderColor: 'border-amber-300',
    accentColor: '#b45309',
    owner: 'M.Sさん（50代・不動産オーナー）',
    location: '東京都大田区',
    propertyType: '一戸建て 2LDK',
    period: '2024年1月〜',
    challenge:
      '空き家になっていた実家をどう活用するか悩んでいた。民泊に興味はあったが、開業から運営体制の構築まで全体像が見えず、信頼できるパートナーを探していた。',
    result:
      '物件選定アドバイスから申請・プロ撮影・清掃体制構築・収益モデル設計まで一括対応。開業3.5ヶ月で初予約を獲得し、安定した収益基盤を確立。',
    stats: [
      { label: '開業までの期間', value: '約3.5ヶ月' },
      { label: '月間平均売上', value: '¥182,000' },
      { label: '稼働率（3ヶ月平均）', value: '78%' },
      { label: 'ゲスト評価', value: '4.87 ★' },
    ],
    quote:
      '空き家のまま放置していた実家が、毎月安定した収入を生む資産に変わりました。開業から運営体制まで全部お任せできて本当に助かりました。',
    image:
      'https://readdy.ai/api/search-image?query=traditional%20Japanese%20detached%20house%20Ota%20City%20Tokyo%20exterior%20warm%20afternoon%20sunlight%20clean%20garden%20wooden%20facade%20residential%20neighborhood%20peaceful&width=600&height=400&seq=case-full-01&orientation=landscape',
  },
  {
    plan: '運営代行',
    planKey: 'management',
    icon: 'ri-calendar-check-line',
    badgeColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    borderColor: 'border-emerald-300',
    accentColor: '#047857',
    owner: 'Y.Nさん（30代・海外在住）',
    location: '東京都渋谷区',
    propertyType: 'ワンルーム マンション',
    period: '2023年9月〜',
    challenge:
      '海外赴任中で東京の物件を管理できなかった。すでに民泊許可は取得済みだったが、ゲスト対応・清掃・価格調整を全て任せられるパートナーが必要だった。',
    result:
      '予約管理・ゲスト対応・清掃手配・動的価格設定を完全代行。オーナーは月次レポートを確認するだけで、海外にいながら安定した収益を得られている。',
    stats: [
      { label: '月間平均売上', value: '¥145,000' },
      { label: '稼働率（6ヶ月平均）', value: '81%' },
      { label: 'ゲスト評価', value: '4.92 ★' },
      { label: 'オーナー対応時間', value: '月0時間' },
    ],
    quote:
      '海外にいても全く心配なく運営できています。毎月レポートが届くだけで、収益が振り込まれる。これが理想の形でした。',
    image:
      'https://readdy.ai/api/search-image?query=stylish%20modern%20studio%20apartment%20Shibuya%20Tokyo%20interior%20design%20minimalist%20chic%20urban%20living%20space%20warm%20lighting%20contemporary%20furniture%20clean&width=600&height=400&seq=case-mgmt-01&orientation=landscape',
  },
];

export default function PlanCaseStudiesSection() {
  const [activePlan, setActivePlan] = useState<'launch' | 'full' | 'management'>('full');

  const active = caseStudies.find((c) => c.planKey === activePlan)!;

  const handleConsult = () => {
    const section = document.getElementById('consultation-form');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.dispatchEvent(
        new CustomEvent('diagnosis-plan-selected', { detail: { plan: active.plan } })
      );
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-full mb-4">
            実績・事例
          </span>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            このプランを選んだ方の事例
          </h2>
          <p className="text-lg text-slate-500">
            実際にご利用いただいたオーナー様の声と成果をご紹介します
          </p>
        </div>

        {/* Plan Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-slate-100 rounded-2xl p-1.5 gap-1">
            {caseStudies.map((c) => (
              <button
                key={c.planKey}
                onClick={() => setActivePlan(c.planKey)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activePlan === c.planKey
                    ? 'bg-white shadow-md text-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${c.icon} text-base`} style={{ color: activePlan === c.planKey ? c.accentColor : undefined }}></i>
                </div>
                {c.plan}
              </button>
            ))}
          </div>
        </div>

        {/* Case Card */}
        <div
          key={active.planKey}
          className={`bg-white rounded-3xl border-2 ${active.borderColor} shadow-xl overflow-hidden`}
          style={{ animation: 'fadeInUp 0.35s ease' }}
        >
          {/* Top image + badge */}
          <div className="relative h-56 overflow-hidden">
            <img
              src={active.image}
              alt={`${active.plan}の事例`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute top-4 left-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${active.badgeBg} ${active.badgeColor}`}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${active.icon} text-sm`}></i>
                </div>
                {active.plan}
              </span>
            </div>
            <div className="absolute bottom-4 left-5 right-5">
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-user-line', text: active.owner },
                  { icon: 'ri-map-pin-line', text: active.location },
                  { icon: 'ri-home-line', text: active.propertyType },
                ].map((item) => (
                  <span key={item.text} className="flex items-center gap-1.5 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${item.icon} text-xs`}></i>
                    </div>
                    {item.text}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Challenge */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-red-50 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  </div>
                  <span className="text-sm font-bold text-slate-700">ご相談前の課題</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-xl p-4">
                  {active.challenge}
                </p>
              </div>

              {/* Result */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-sm"></i>
                  </div>
                  <span className="text-sm font-bold text-slate-700">サポート内容と成果</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed bg-emerald-50 rounded-xl p-4">
                  {active.result}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {active.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center bg-slate-50 rounded-2xl py-5 px-3 border border-slate-100"
                >
                  <div
                    className="text-2xl font-bold mb-1"
                    style={{ color: active.accentColor }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-xs text-slate-500 leading-tight">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div
              className="relative rounded-2xl p-6 mb-8"
              style={{ background: `${active.accentColor}08`, border: `1px solid ${active.accentColor}25` }}
            >
              <div
                className="absolute top-3 left-5 text-5xl font-serif leading-none opacity-20"
                style={{ color: active.accentColor }}
              >
                "
              </div>
              <p className="relative text-sm text-slate-700 leading-relaxed italic pl-4">
                {active.quote}
              </p>
              <p className="text-xs text-slate-400 mt-3 pl-4">— {active.owner}</p>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleConsult}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 text-white font-bold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: active.accentColor }}
              >
                <i className="ri-calendar-line text-lg"></i>
                {active.plan}について相談する
              </button>
              <a
                href="/ota-city-case-study"
                className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-article-line text-lg"></i>
                詳細な事例を読む
              </a>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm text-slate-400 mt-6">
          ※ 掲載内容はご本人の許可を得た上で公開しています。数値は実績の一例です。
        </p>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
